'use client';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';
import { FeedList } from '@/app/components/shared/Community';
import { Tabs, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { useStore } from '@/app/providers/store';
import { CommunityEnum } from '@/app/types';
import { reportEvent } from '@/app/utils';
import { useCollectData } from '../_hooks/use-collect-data';
import { apiGetMyCollect } from '../apis';
import { COMMUNITY_SEARCH_OPTIONS, LIKES_SUBTYPE, PROFILE_SEARCH_OPTIONS, PROFILE_TAB_ENUM } from '../constant';

export const Collected = () => {
  const searchOptions = PROFILE_SEARCH_OPTIONS();
  const syncFeedDetailConfig = useStore((state) => state.syncFeedDetailConfig);

  const {
    state,
    setState,
    loading,
    handleSelectedCreation,
    vote,
    unVote,
    incrementCompleteTimes,
    handleLeave,
    handleReferenceDetail,
    onExpose,
  } = useCollectData({
    fetchData: apiGetMyCollect,
    searchOptions,
    infinityList: true,
  });
  const t = useTranslations();

  useEffect(() => {
    reportEvent('page_view', {
      page_name: 'my_collected_page',
    });
  }, []);

  const commonCommunityProps = useMemo(() => {
    return {
      handleVote: vote,
      handleUnVote: unVote,
    };
  }, [vote, unVote]);

  useEffect(() => {
    syncFeedDetailConfig({
      ...commonCommunityProps,
      onClose: () => {
        handleLeave();
        setState({ selectedAsset: undefined });
      },
      onVideoPlayEnd: incrementCompleteTimes,
      thumbScrollListConfig: {
        list: state.items,
        hasMore: state.hasMore,
        onLoadMore: () => setState({ page: state.page + 1 }),
      },
    });
  }, [state.items, state.hasMore]);

  return (
    <div className="flex flex-1 flex-col">
      <div className="sticky top-[52px] z-20 bg-system-bg08 pb-6 text-xl sm:top-10">
        <div className="hide-scrollbar flex items-center overflow-x-scroll">
          <Tabs
            value={state.type}
            onValueChange={(value) => {
              if (value !== state.type) {
                setState({
                  type: value as COMMUNITY_SEARCH_OPTIONS,
                  page: 1,
                  items: [],
                  referenceList: [],
                });
                reportEvent('me_page_element_click', {
                  element_name: PROFILE_TAB_ENUM.COLLECT,
                  subtype: LIKES_SUBTYPE[value as COMMUNITY_SEARCH_OPTIONS],
                });
              }
            }}
          >
            <TabsList className="flex h-fit w-fit min-w-full flex-nowrap gap-2 overflow-x-scroll rounded-8 bg-transparent p-0 pr-6">
              {searchOptions.map((option) => (
                <TabsTrigger
                  key={option.key}
                  className="h-7 min-w-[62px] flex-shrink-0 whitespace-nowrap rounded-8 bg-system-white/10 px-3 py-2 font-normal text-sm text-white capitalize data-[state=active]:bg-system-white data-[state=active]:text-system-black"
                  value={option.key}
                >
                  {option.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      <FeedList
        inspAssets={state.items}
        handleDetail={(id) =>
          state.type === COMMUNITY_SEARCH_OPTIONS.REFERENCE ? handleReferenceDetail(id) : handleSelectedCreation(id)
        }
        onExpose={onExpose}
        handleUnVote={unVote}
        handleVote={vote}
        referenceList={state.referenceList}
        active={state.type === 'all' ? CommunityEnum.MediaAsset : (state.type as CommunityEnum)}
        hasMore={state.hasMore}
        isLoading={loading}
        hideEmptyVoteCount={true}
        onLoadMore={async () => {
          setState({
            page: state.page + 1,
          });
        }}
        emptyListText={t('profile.emptyCollect')}
      />
    </div>
  );
};
